import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useErrorHandler } from '@/hooks/use-error-handler';
import graphqlClient from '@/graphql/graphql-client';
import { requestTemplateDocxs, type TemplateDocxRecord } from '@/graphql/queries/templates';
import {
  INSERT_TEMPLATE_DOCX_MUTATION,
  UPDATE_TEMPLATE_DOCX_MUTATION,
} from '@/graphql/mutations/templates';
import type {
  TemplateDocx,
  TemplateDocxInsertInput,
  TemplateDocxUpdateInput,
} from '../types/templates.types';

const strings = (values: Array<string | null> | null | undefined): string[] | undefined => {
  if (!values) return undefined;
  return values.filter((value): value is string => value != null);
};

const toTemplateDocx = (d: TemplateDocxRecord): TemplateDocx => ({
  id: d.ItemId,
  organizationIds: strings(d.OrganizationIds),
  templateId: d.TemplateId ?? '',
  originalUrl: d.OriginalUrl ?? undefined,
  originalKey: d.OriginalKey ?? undefined,
  originalName: d.OriginalName ?? undefined,
  sizeBytes: d.SizeBytes ?? undefined,
  tokenizedUrl: d.TokenizedUrl ?? undefined,
  tokenizedKey: d.TokenizedKey ?? undefined,
  tokenVersion: d.TokenVersion ?? undefined,
  status: d.Status ?? undefined,
});

export const fetchTemplateDocxsByTemplateId = async (templateId: string) => {
  try {
    const response = await requestTemplateDocxs({
      where: { TemplateId: { eq: templateId } },
      paging: { pageNo: 1, pageSize: 10 },
    });
    const result = response?.getTemplateDocxs || { items: [], totalCount: 0 };
    return { ...result, items: (result.items || []).map(toTemplateDocx) };
  } catch (error) {
    console.error('Error fetching template docxs:', error);
    return { items: [] as TemplateDocx[], totalCount: 0 };
  }
};

export const useGetTemplateDocxsByTemplateId = (templateId: string | undefined) => {
  return useGlobalQuery({
    queryKey: ['templateDocxs', templateId],
    queryFn: () => fetchTemplateDocxsByTemplateId(templateId!),
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateTemplateDocx = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (input: TemplateDocxInsertInput) =>
      graphqlClient.request(INSERT_TEMPLATE_DOCX_MUTATION, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateDocxs'] });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to save document' });
    },
  });
};

export const useUpdateTemplateDocx = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: TemplateDocxUpdateInput }) =>
      graphqlClient.request(UPDATE_TEMPLATE_DOCX_MUTATION, { ItemId: itemId, ...input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateDocxs'] });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to update document' });
    },
  });
};
